import { Dispatch, useEffect } from 'react'

import { getConversation } from 'conversations/modules/conversation/selectors'
import { getEndUser } from 'conversations/modules/endUser/selectors'
import { getHasPushCluster } from 'conversations/modules/gates/selectors'
import { getSocketSetupComplete } from 'conversations/modules/socket/selectors'
import { getOrgId } from 'conversations/modules/user/selectors'
import { getDisplayName } from 'conversations/utils/nameUtils'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { receiveEndUserUpdate } from 'shared/store/actions/endUser'
import { EndUser } from 'shared/typings/endUser/EndUser'

import { CLUSTER, getDriftWebSocket } from '@driftt/drift-ws'

import { RootAction, RootState } from 'typesafe-actions'

import { useCurrentConversation } from './useConversation'

const useSubscribeEndUser = (id: string | undefined) => {
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const orgId = useSelector<RootState, number | undefined>(getOrgId)
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const hasPushCluster = useSelector<RootState, boolean>(() => getHasPushCluster())

  useEffect(() => {
    if (id && orgId && socketSetupComplete) {
      const ws = getDriftWebSocket()
      const subscription = ws
        .on<EndUser>({
          cluster: hasPushCluster ? CLUSTER.PUSH : CLUSTER.CHAT,
          channelName: `subscribe-end-user:${orgId}-${id}`,
          messageName: 'end-user-updated',
        })
        .subscribe((endUser) => {
          dispatch(receiveEndUserUpdate([endUser]))
        })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [id, orgId, dispatch, socketSetupComplete, hasPushCluster])
}

export const useCurrentEndUser: () => EndUser | undefined = () => {
  const endUserId = useCurrentEndUserId()
  const endUser = useSelector<RootState, EndUser | undefined>(getEndUser(endUserId))
  useSubscribeEndUser(endUser?.id)
  return endUser
}

export const useCurrentEndUserIp: () => string | undefined = () => {
  const conversation = useCurrentConversation()
  return conversation?.context?.ip
}

export const useCurrentEndUserId: () => string | undefined = () => {
  const params = useParams<{ endUserId?: string; conversationId?: string }>()
  const conversation = useSelector(getConversation(Number(params.conversationId)))

  return params.conversationId ? conversation?.endUserId : params.endUserId ?? undefined
}

export const useCurrentEndUserEmail: () => string | undefined = () => {
  const endUser = useCurrentEndUser()
  return endUser?.email
}

export const useCurrentEndUserName: () => string | undefined = () => {
  return useCurrentEndUser()?.name
}

export const useCurrentEndUserDisplayName: () => string = () => {
  const endUserId = useCurrentEndUserId()
  return useSelector((state: RootState): string => getDisplayName(getEndUser(endUserId)(state)))
}

export const useEndUser = (id: string | undefined): EndUser | undefined => {
  const endUser = useSelector<RootState, EndUser | undefined>(getEndUser(id))
  useSubscribeEndUser(id)

  return endUser
}
